import { ReactElement, useEffect, useState } from "react";
import Auth from "../../../Auth/Auth";
import PortfolioService from "../../../services/PortfolioService";
import CarteraRatioTable from "./CarteraRatioTable/CarteraRatioTable";
import { DefaultButton, Dropdown, IDropdownOption, Spinner, TooltipHost } from "@fluentui/react";
import { TButtonsTable, TRatiosCarterasData } from "./types/CarteraRatioTypes";
import SavingsPlanService, { ActiveSubscriptionAndSavingPlans } from "../../../services/SavingsPlanService";
import { dropdownStyles } from "./Styles/DropDownStyles";
import { MapRatiosToGraph, TDataSeries } from "./Util/MapRatiosToGraph";
import HighchartsReactCustom, { TGraphDataChart } from "./HighChartsReactCustom/HighChartsReactCustom";
import React from "react";
import { useHistory } from "react-router-dom";
import { ToolTip_Linear_Return, ToolTip_Volatility, ToolTip_Max_Drawdown, ToolTip_Downside_Deviation, ToolTip_Time_To_Recovery, ToolTip_Omega, ToolTip_Sharpe, ToolTip_Calmar, ToolTip_Sortino, Header_Description, Header_Description_Need_To_Subscribe } from "./Util/texts";
import UsersSubscriptionService from "../../../services/UsersSubscriptionService";
import CustomTeachingBubble from "../../../components/CustomTeachingBubble/CustomTeachingBubble";
import { tipImages, tipTexts } from "./TooltipData/TooltipData";
import { useBoolean } from "@fluentui/react-hooks";

const BASE_CLASS = "portfolio_file";

const FICHA_CARTERA_SUBSCRIPTION_ID = 52;

const PortfolioFile = () => {
    const [tooltipRatio, { setTrue: showTooltipRatio, setFalse: hideTooltipRatio }] = useBoolean(false);
    const [options, setOptions] = useState<IDropdownOption[]>();
    const [tablePeriod, setTablePeriod] = useState<string>('Inception');

    // SELECTED
    const [selectedWallets, setSelectedWallets] = useState<IDropdownOption[]>([]);

    // SAVED
    const [walletsSaved, setWalletsSaved] = useState<TRatiosCarterasData[]>([]);
    const [graphRatiosSaved, setGraphRatiosSaved] = useState<TGraphDataChart[]>([]);

    // STATE BUTTONS GRAPHS
    const [graphRatio, setGraphRatio] = useState<TButtonsTable>({text: 'Retorno anualizado', value: 'linear_return'});
    const [secondGraphRatio, setSecondGraphRatio] = useState<TButtonsTable>({text: 'Volatilidad', value: 'volatility'});

    // STATE DATA GRAPHS
    const [graphRatioData, setGraphRatioData] = useState<TGraphDataChart[]>([]);
    const [secondGraphRatioData, setSecondGraphRatioData] = useState<TGraphDataChart[]>([]);
    const [walletsData, setWalletsData] = useState<TRatiosCarterasData[]>([]);

    const [activePlan, setActivePlan] = useState(Auth.getActivePlan());
    const [isloadingRatioCartera, setIsLoadingRatioCartera] = useState<boolean>(true);
    const [isloadingGraph, setIsloadingGraph] = useState<boolean>(true);
    const [isloadingSecondGraph, setIsloadingSecondGraph] = useState<boolean>(true);

    // SUBSCRIPTION STATUS 
    const [ subscriptionStatus, setSubscriptionStatus ] = useState<boolean>(true); // TODO: Cambiar a false cuando susbscription process esté en marcha
    
    const user = Auth.getUserProfile();

    const history = useHistory();
    
    const getRatiosCarteras = async ({name, contrato}:{name: string, contrato: string}): Promise<TRatiosCarterasData> => {
        const result = await PortfolioService.getRatioCarteraByNomcontrato(contrato)
        const ratiosData: TRatiosCarterasData = {name: name, data: result};
        return ratiosData;   
    };

    const getRatioFromPeriod = async ({ratio, contrato, walletName}: {ratio: string, contrato: string, walletName: string}): Promise<TGraphDataChart> => {
        try {
            const result = await PortfolioService.getRatioCarteraByRatioAndPeriodAndNomcontrato({ratio: ratio, period: tablePeriod, nomcontrato: contrato})
            const dataGraph = MapRatiosToGraph({ratios: result});
            const graphDataWithName: TGraphDataChart = { walletName: walletName, ratio: ratio, contrato: contrato, period: tablePeriod, data: dataGraph };
            return graphDataWithName;
        } catch(error) {
            console.error(error)
            const dataEmpty = [] as TDataSeries[]
            const graphDataWithNameEmpty: TGraphDataChart = {walletName: walletName, ratio: ratio, contrato: contrato, period: tablePeriod, data: dataEmpty };
            return graphDataWithNameEmpty;
            }
    };

    const handleRenderTitle = (items: IDropdownOption[] | undefined): ReactElement<any, any> =>
    {
        if(items)
        {
            if(items.length === 1)
            {
                return <React.Fragment>{`${items[0].text}`}</React.Fragment>;
            }
            return <React.Fragment>{"Cartera resumen"}</React.Fragment>;
        }
        return <React.Fragment></React.Fragment>;
    };

    const onUpdateSelectionWallets = async (event: React.FormEvent<HTMLDivElement>, option: IDropdownOption | undefined): Promise<void> => {
        setIsLoadingRatioCartera(true);
        if (!option) {
            console.warn("Option is undefined");
            return;
        }

        setSelectedWallets(prevState =>
            option.selected
                ? [...prevState, option]
                : prevState.filter(item => item.key !== option.key)
        );
    };

    const updateWallets = async () => {
        const walletsDataFiltered = selectedWallets.map(async (selection: IDropdownOption) => {
            const walletExist = 
                walletsData.find((wallet: TRatiosCarterasData) => wallet.name === selection.text) ||
                walletsSaved.find((ratioWallet: TRatiosCarterasData) => ratioWallet.name === selection.text);

            return walletExist ?? getRatiosCarteras({ name: selection.text, contrato: selection.key.toString() })
                .then(result => {
                    setWalletsSaved(prevState =>
                        prevState.some(item => item.name === result.name) ? prevState : [...prevState, result]
                    );
        
                    return result;
                })
                .catch (err => {
                    console.error(err);
                    return null;
                }) 
        });

        setWalletsData((await Promise.all(walletsDataFiltered))
            .filter((item): item is TRatiosCarterasData => { 
                setIsLoadingRatioCartera(false);
                return item !== null;
            }));
    };

    const updateGraphs = async ({ graphRatioSelected, setState, setLoader } : { 
        graphRatioSelected: TButtonsTable, 
        setState: Function,
        setLoader: Function
    }) => {
        setLoader(true);
        const graphsData = selectedWallets.map(async (selection: IDropdownOption) => {
            const foundGraphSaved = graphRatiosSaved?.find(graphData =>
                    graphData.walletName === selection.text &&
                    graphData.ratio === graphRatioSelected.value &&
                    graphData.period === tablePeriod);

            return foundGraphSaved ?? getRatioFromPeriod({ 
                    ratio: graphRatioSelected.value, 
                    walletName: selection.text, 
                    contrato: selection.key.toString() 
                }).then(resultFromService => {
                    setGraphRatiosSaved(prevState => {                    
                        const exists = prevState.find(item =>
                            item.walletName === resultFromService.walletName &&
                            item.period === resultFromService.period &&
                            item.ratio === resultFromService.ratio
                        );
                        return exists
                        ? prevState.map(item =>
                            item.walletName === resultFromService.walletName &&
                            item.period === resultFromService.period &&
                            item.ratio === resultFromService.ratio
                                ? { ...item, ...resultFromService }
                                : item
                          )
                        : [...prevState, resultFromService];
                    });
                    return resultFromService;
                }).catch(err => {
                    console.error("Error obteniendo ratio:", err);
                    return null;
                })
        });
        setState([]);

        setState((await Promise.all(graphsData)).filter((item): item is TGraphDataChart => {
            setLoader(false);
            return item !== null;
        }));
    };

    useEffect(() => {
            updateGraphs({ graphRatioSelected: graphRatio, 
                setState: setGraphRatioData, setLoader: setIsloadingGraph });
            updateGraphs({ graphRatioSelected: secondGraphRatio, 
                setState: setSecondGraphRatioData, setLoader: setIsloadingSecondGraph });
    }, [selectedWallets, tablePeriod, graphRatio, secondGraphRatio])

    useEffect(() => {
        updateWallets();
    }, [selectedWallets])

    useEffect(() => {
        getRatioFromPeriod({ratio: 'linear_return', contrato: activePlan.finametrixContract, walletName: activePlan.name})
            .then(result => {
                setGraphRatioData([result]);
                setIsloadingGraph(false);
            });
        getRatioFromPeriod({ratio: 'volatility', contrato: activePlan.finametrixContract, walletName: activePlan.name})
            .then(result => {
                setSecondGraphRatioData([result]);
                setIsloadingSecondGraph(false);
            });
        getRatiosCarteras({name: activePlan.name, contrato: activePlan.finametrixContract})
            .then(result => {
                setWalletsData([result]);
                setWalletsSaved([result]);
                setIsLoadingRatioCartera(false);
                setSelectedWallets([{
                    key: result.data[0].nomcontrato,
                    text: result.name,
                    id: activePlan.id.toString()
                }]);
            }
        );

        // TODO: Descomentarlo cuando susbscription process esté en marcha
        // UsersSubscriptionService.getUsersSubscriptionStatusByUserIdAndSubscriptionId({
        //     userId: user.id,
        //     subscriptionId: FICHA_CARTERA_SUBSCRIPTION_ID,
        // }).then(result => {
        //     setSubscriptionStatus(result.subscriptionStatus.status)
        // })

        SavingsPlanService.getActiveSubscriptionAndSavingPlan(user.id).then((response: ActiveSubscriptionAndSavingPlans[]) => {
            const aux: IDropdownOption[] = response
                .map(savingsPlan => ({
                    key: savingsPlan.finametrixContract,
                    text: savingsPlan.planName,
                    id: savingsPlan.id.toString()
                }));
            setOptions(aux);
        });
    }, []);

    const buttonsTableList: TButtonsTable[] = [
        { text: '1M', value: '1M', },
        { text: '3M', value: '3M', },
        { text: '6M', value: '6M', },
        { text: '9M', value: '9M', },
        { text: '1Y', value: '1Y', },
        { text: '2Y', value: '2Y', },
        { text: '3Y', value: '3Y', },
        { text: 'YTD', value: 'YTD', },
        { text: 'Inicio', value: 'Inception', },
    ];

    const buttonsGraphList: TButtonsTable[] = [
        { text: 'Retorno anualizado', value: 'linear_return', toolTipHostContent: ToolTip_Linear_Return },
        { text: 'Volatilidad', value: 'volatility', toolTipHostContent: ToolTip_Volatility },
        { text: 'Mayor caída', value: 'max_drawdown', toolTipHostContent: ToolTip_Max_Drawdown },
        { text: 'Downside risk', value: 'downside_deviation', toolTipHostContent: ToolTip_Downside_Deviation },
        { text: 'Tiempo de recuperación', value: 'time_to_recovery', toolTipHostContent: ToolTip_Time_To_Recovery },
        { text: 'Omega', value: 'omega', toolTipHostContent: ToolTip_Omega, },
        { text: 'Sharpe', value: 'sharpe', toolTipHostContent: ToolTip_Sharpe },
        { text: 'Calmar', value: 'calmar', toolTipHostContent: ToolTip_Calmar },
        { text: 'Sortino', value: 'sortino', toolTipHostContent: ToolTip_Sortino },
    ];

    return (
        <div className={BASE_CLASS}>
            {tooltipRatio && <CustomTeachingBubble texts={tipTexts} images={tipImages} targetIdentifier="tip_location" showTooltip={hideTooltipRatio} />}
            <div className={`${BASE_CLASS}__header`}>
                <div className={`${BASE_CLASS}__header_texts`}>
                    <h2>Ficha Cartera</h2>

                    <p className={`${BASE_CLASS}__header__description`}>
                        {Header_Description}
                    </p>
                    {!subscriptionStatus &&
                    <p className={`${BASE_CLASS}__header__description alert`}>
                        {Header_Description_Need_To_Subscribe}
                    </p>
                    }
                </div>
                <div className={`${BASE_CLASS}__header__buttons`}>
                    {/* TODO: Hay que cambiar el disabled a false cuando el proceso de subscripción esté en marcha */}
                    <DefaultButton className="button tiny-stretch-button" disabled={true} onClick={() => history.push(`/portfolio/service-suscription/${FICHA_CARTERA_SUBSCRIPTION_ID}`)}>
                        Suscribirse
                    </DefaultButton>
                </div>
            </div>
            {subscriptionStatus && 
            <div className={`${BASE_CLASS}__body__container`}>

                <div className={`${BASE_CLASS}__left__container`}>
                    <div className={`${BASE_CLASS}__dropdown__container`}>
                        <Dropdown placeholder={activePlan.name}
                            className={`${BASE_CLASS}__dropdown`}
                            options={options as IDropdownOption[]}
                            defaultSelectedKeys={[activePlan.finametrixContract]}
                            onRenderTitle={handleRenderTitle}
                            onChange={onUpdateSelectionWallets}
                            styles={dropdownStyles}
                            multiSelect
                        />
                    </div>
                    <div className={`${BASE_CLASS}__card__container`}>
                        <div className={`${BASE_CLASS}__table__header`}>
                            <h3>Ratios Cartera</h3>
                            <span>Fecha: {new Date(walletsData[0]?.data[0].dateCalculation).toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric" })}</span>
                        </div>
                        {isloadingRatioCartera ? <Spinner size={3} style={{ padding: "1em" }} /> :
                            <CarteraRatioTable ratios={walletsData} tablePeriod={tablePeriod} /> 
                        }
                    </div>
                </div>
                <div className={`${BASE_CLASS}__right__container`}>
                    <div id="table_perdiod_id" className={`${BASE_CLASS}__buttons__period`}>
                        {buttonsTableList.map((buttonTable: TButtonsTable) => (
                            <button className={`${tablePeriod === buttonTable.value ? 'active' : ''} fileButtons`} 
                                onClick={() => {
                                    setTablePeriod(buttonTable.value)
                                }}>{buttonTable.text}</button>
                        ))}
                        <img id="tip_location" onMouseEnter={showTooltipRatio} onClick={showTooltipRatio} style={{ verticalAlign: 'bottom' }} src="/icons/informationBlack.svg" alt="Información" />
                    </div>
                    <div className={`${BASE_CLASS}__graph__container`}>
                        {isloadingGraph && <Spinner size={3} style={{ padding: "1em" }} />}
                        <div className={`${BASE_CLASS}__graph__card`}>
                            <div className={`${BASE_CLASS}__buttons__graph`}>
                                {buttonsGraphList.map((buttonTable: TButtonsTable) => (
                                    <TooltipHost content={buttonTable.toolTipHostContent}>
                                        <button className={`${graphRatio.value === buttonTable.value ? 'active' : ''}`} 
                                            onClick={() => {
                                                setGraphRatio({
                                                    text: buttonTable.text,
                                                    value: buttonTable.value
                                                })
                                            }}>
                                                {buttonTable.text}
                                        </button>
                                    </TooltipHost>
                                ))}
                            </div>
                            <HighchartsReactCustom name={graphRatio.text} graphDataList={graphRatioData} />
                        </div>
                        {isloadingSecondGraph && <Spinner size={3} style={{ padding: "1em" }} />}
                        <div className={`${BASE_CLASS}__graph__card`}>
                            <div className={`${BASE_CLASS}__buttons__graph`}>
                                {buttonsGraphList.map((buttonTable: TButtonsTable) => (
                                    <TooltipHost content={buttonTable.toolTipHostContent}>
                                        <button className={`${secondGraphRatio.value === buttonTable.value ? 'active' : ''}`} 
                                            onClick={() => {
                                                setSecondGraphRatio({
                                                    text: buttonTable.text,
                                                    value: buttonTable.value
                                                })
                                            }}>
                                                {buttonTable.text}
                                        </button>
                                    </TooltipHost>
                                ))}
                            </div>
                            <HighchartsReactCustom name={secondGraphRatio.text} graphDataList={secondGraphRatioData} />
                        </div>
                    </div>
                </div>
            </div>
            }
        </div>
    )
}

export default PortfolioFile;